import {getChannelsByProps_Api} from 'apis'
import {getId, getType} from 'apis/model/base'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import CustomLazyPaginationWrapper from 'modules/asyncCache/components/CustomLazyPaginationWrapper'
import React, {useContext, useMemo} from 'react'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import OrganizationContainerContext from '../OrganizationContainerContext'
import EmptyHolder from '../../../../components/EmptyHolder'
import {SiderBlockHeader} from '../components/SiderBlockHeader'
import {SiderBlockCard} from '../components/SiderBlockCard'
import {RiSignalTowerFill} from 'react-icons/ri'
import {FaWandMagicSparkles} from 'react-icons/fa6'
import {FiChevronsRight} from 'react-icons/fi'
import {Link} from 'react-router-dom'
import {Skeleton} from 'antd'
import getLinkToDetail from 'helpers/getLinkToDetail'
import getTitle from 'helpers/getTitle'
import Pure from 'components/Pure'
import {LayoutContext} from 'components/layouts/Default/LayoutContext'
import {useHistory} from 'modules/navigation/useRouter'
import {scrollToRow} from '../../Missal/functions/scrollToRow'
import Translate from 'modules/local/Translate'
import {FollowButton} from 'components/Feed/FollowButton'

// ─── Skeletons ────────────────────────────────────────────────────────────────
// Card giờ chỉ còn 1 dòng thông tin (không còn feed preview bên dưới), skeleton
// rút gọn theo - và xếp lưới 2 cột như card thật (renderWrap) để không "nhảy"
// layout lúc chuyển từ loading sang có dữ liệu.

const ChannelSkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {[0, 1, 2, 3].map((i) => (
            <div key={i} className="p-3 flex items-center gap-3 rounded-xl border border-color-50 background">
                <Skeleton.Avatar active size={40} shape="square"/>
                <div className="flex-1">
                    <Skeleton active title={{width: '70%'}} paragraph={{rows: 1, width: '40%'}}/>
                </div>
            </div>
        ))}
    </div>
)

// ─── Channel card ─────────────────────────────────────────────────────────────
// Trước đây mỗi card tự fetch thêm 4 tin mẫu (feed_fetchFeedByContainer_Api) để
// preview bên dưới - phản hồi thực tế: "đừng load list tin sample nữa, hãy list
// các channel thôi" (tốn N request phụ, không cần thiết khi mục đích chỉ là
// liệt kê/theo dõi kênh - xem tin đã có ở tab "Tin tức"). Card giờ gọn lại 1
// hàng ngang: icon kênh + tên/số theo dõi + nút theo dõi, giống tinh thần card
// bên sidebar (renderChannelItemSider) nhưng đủ rộng cho layout 2 cột ở đây.
const OrgChannelCard = ({item}) => {
    const t = useTranslate()

    if (!item) return null

    return (
        <div className="p-3 flex items-center gap-3 background rounded-xl border border-color-50 shadow-sm hover:shadow-md hover:scale-3xs transition-all">
            <div className="w-10 h-10 flex flex-center flex-shrink-0 bg-blue-50 rounded-lg">
                <RiSignalTowerFill size={18} className="text-blue-500"/>
            </div>
            <div className="flex flex-col flex-1 min-w-0 gap-0.5">
                <Link
                    to={getLinkToDetail(item)}
                    className="font-bold text-sm text-color-000 leading-snug no-underline hover:text-primary max-lines-1">
                    {getTitle(item)}
                </Link>
                <div className="flex items-center gap-2 flex-wrap">
                    {Number(item?.total_followers) > 0 && (
                        <span className="text-xs text-color-300 tracking-wider lowercase">
                            <strong className="font-semibold text-slate-600">
                                {Number(item?.total_followers).toLocaleString()}
                            </strong>{' '}{t('followers')}
                        </span>
                    )}
                    {!!item?.selected_by_editor && (
                        <span className="flex items-center gap-1 px-2 py-0.5 text-2xs font-semibold text-blue-600 bg-blue-50 rounded-full border border-blue-100">
                            <FaWandMagicSparkles size={8}/>
                            {t("editor's choice")}
                        </span>
                    )}
                </div>
            </div>
            <div className="flex-shrink-0">
                <FollowButton size="small" item={item} className="text-xs"/>
            </div>
        </div>
    )
}

// ─── Channel tab main component ───────────────────────────────────────────────

const OrganizationChannelList = ({Wrapper = 'div'}) => {
    const t = useTranslate()
    const {organization: item} = useContext(OrganizationContainerContext)
    const [id, type] = [getId(item), getType(item)]

    const apiInfo = getChannelsByProps_Api

    const pagerKey = useMemo(
        () => [apiInfo.path, type, id].join('/'),
        [id, type]
    )

    if (_.isEmpty(item)) return null

    return (
        <Wrapper className="flex flex-col gap-6 pb-8">
            <div className="flex flex-col flex-1">
                <div className="font-semibold text-lg text-gray-800 tracking-wide">
                    {t('channel')}
                </div>
                <div className="w-20 h-1 my-1 rounded-full bg-gray-400"/>
                <span className="text-color-300 tracking-wider">
                    {t('channel introduction description')}
                </span>
            </div>
            <Pure input={[id, type]}>
                <CustomLazyPaginationWrapper
                    alwaysVisible={true}
                    id={pagerKey}
                    apiInfo={apiInfo}
                    query={bindQueryParams([{id}, {type}])}
                    renderItem={(channel) => <OrgChannelCard key={getId(channel)} item={channel}/>}
                    renderLoading={() => <ChannelSkeleton/>}
                    renderWrap={(child, __, count) => {
                        if (!count) {
                            return (
                                <EmptyHolder
                                    className="py-6"
                                    title={t('channel')}
                                    subTitle={t('No data available')}
                                />
                            )
                        }
                        return (
                            <div className="flex flex-col gap-3">
                                {child}
                            </div>
                        )
                    }}
                />
            </Pure>
        </Wrapper>
    )
}

// ─── Sidebar variant ──────────────────────────────────────────────────────────

const renderChannelItemSider = ({isLg}) => (item, index) => (
    <Translate key={index}>
        {(t) => (
            <div className="px-3 py-2 background-50 hover:scale-2xs hover:shadow-md rounded-xl">
                <div className="mb-2 pb-2 border-b">
                    <Link
                        to={getLinkToDetail(item)}
                        className="font-bold text-base text-color-000 tracking-wide max-lines-1 no-underline">
                        {getTitle(item)}
                    </Link>
                </div>
                <div className="flex items-center justify-between">
                    <span className="text-xs text-color-100 italic tracking-wider lowercase">
                        <strong>{item?.total_followers}</strong> {t('followers')}
                    </span>
                    {!!item?.selected_by_editor && (
                        <span
                            className="h-6 px-3 py-1 flex flex-center font-medium text-xs text-blue-500 bg-blue-100 tracking-wide rounded-full">
                            <FaWandMagicSparkles size={10} className="mr-1"/>
                            {!!isLg && t("editor's choice")}
                        </span>
                    )}
                </div>
            </div>
        )}
    </Translate>
)

export const OrganizationChannelSider = ({Wrapper = 'div'}) => {
    const t = useTranslate()
    const {organization: item} = useContext(OrganizationContainerContext)
    const {isLg} = useContext(LayoutContext)
    const history = useHistory()
    const [id, type] = [getId(item), getType(item)]

    if (_.isEmpty(item)) return null

    return (
        <CustomLazyPaginationWrapper
            id={[getChannelsByProps_Api.path, type, id].join('/')}
            sample={2}
            auto={true}
            apiInfo={getChannelsByProps_Api}
            query={bindQueryParams([{id}, {type}])}
            renderItem={renderChannelItemSider({isLg})}
            renderWrap={(children, __, count) => !!count && (
                <SiderBlockCard>
                    <SiderBlockHeader
                        icon={RiSignalTowerFill}
                        iconBgClassName="bg-blue-100"
                        iconColorClassName="text-blue-600"
                        title={t('channels')}
                    />
                    {children}
                </SiderBlockCard>
            )}
            // Trước đây hiện "Xem thêm" VÔ ĐIỀU KIỆN (bỏ qua hẳn `record` truyền vào) - hiện cả khi tổ
            // chức chỉ có đúng 1 kênh, không còn gì để xem thêm (bug thực tế đã gặp). Chỉ hiện khi còn
            // trang kế (record.nextUrl) HOẶC trang đầu đã fetch nhiều hơn "sample" (2) mục.
            renderLoadMoreButton={(handleLoadMore, record) => {
                const fetchedCount = _.sumBy(
                    Object.values(record?.responses || {}),
                    (r) => _.get(r, 'data.length', 0)
                )
                if (!record?.nextUrl && fetchedCount <= 2) return null
                return (
                    <span
                        className="flex items-center justify-end gap-1 text-color-300 hover:text-color-100 cursor-pointer"
                        onClick={() => {
                            scrollToRow('contentOrg')
                            history.push(getLinkToDetail(item) + '/channels')
                        }}>
                        <span className="text-xs tracking-wider">{t('see more')}</span>
                        <FiChevronsRight size={13} className="my-auto"/>
                    </span>
                )
            }}
        />
    )
}

export default OrganizationChannelList
